// Cliente HTTP y renovación de sesión de la plataforma nueva (crm.wiservet.com).
//
// Compuerta 0 (Copiloto con Mario): "la sesión de Google dura la jornada con
// renovación segura, cierre real al salir, revocable". Causas encontradas en el
// cliente de wiservet.com (src/services/api.ts + src/stores/auth.ts) y corregidas
// aquí:
//
//  1. Al abrir una pestaña nueva, reabrir el navegador o la app instalada, el
//     access token (sessionStorage) no existe y el bootstrap NO intentaba
//     renovar con la cookie httpOnly: el usuario caía al login aunque su sesión
//     (cookie de 7 días) estuviera viva. Aquí el bootstrap siempre intenta una
//     renovación silenciosa.
//  2. La renovación rota la cookie (revoca la sesión vieja). Dos pestañas (o
//     wiservet.com y crm.wiservet.com con la cookie compartida) que renuevan a
//     la vez: la segunda llega con la cookie ya revocada, recibe 401 y se
//     desloguea. Aquí la renovación se serializa entre pestañas (Web Locks) y,
//     si aun así recibe 401, reintenta una vez (el navegador ya tiene la cookie
//     nueva). Además crm usa su propia cookie, solo del host.
//  3. El token se renueva antes de vencer (no solo tras un 401) y al volver a
//     la pestaña, para que una jornada larga no dependa de un error previo.
//
// El access token vive solo en memoria: la persistencia es la cookie httpOnly.

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const REFRESH_URL = '/api/v1/auth/refresh';
const LOCK_NAME = 'wiservet-pn-refresh';

/** Ajustes de tiempos (los tests los acortan). */
export const sessionTuning = {
  /** Espera antes del reintento único de la renovación. */
  retryDelayMs: 400,
  /** Cuánto antes del vencimiento se renueva el access token. */
  refreshSkewMs: 60_000,
};

let accessToken: string | null = null;
let accessExpiresAt = 0;
let inflight: Promise<boolean> | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let onSessionLost: (() => void) | null = null;

/** Registra qué hacer cuando la sesión ya no se puede renovar (ir al login). */
export function setSessionLostHandler(fn: (() => void) | null): void {
  onSessionLost = fn;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Momento (ms epoch) en que vence el access token actual; 0 si no hay. */
export function getAccessTokenExpiresAt(): number {
  return accessExpiresAt;
}

/**
 * Guarda el access token y agenda su renovación anticipada.
 * expiresInSec: segundos de vida que informó el backend (expires_in).
 */
export function setAccessToken(token: string | null, expiresInSec?: number): void {
  accessToken = token;
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (!token) {
    accessExpiresAt = 0;
    return;
  }
  const lifeMs = expiresInSec && expiresInSec > 0 ? expiresInSec * 1000 : 15 * 60_000;
  accessExpiresAt = Date.now() + lifeMs;
  const delay = Math.max(lifeMs - sessionTuning.refreshSkewMs, 5_000);
  proactiveTimer = setTimeout(() => {
    void refreshAccessToken().then((ok) => {
      if (!ok) sessionLost();
    });
  }, delay);
}

/** true si no hay token o vence dentro del margen de renovación. */
export function accessTokenNeedsRefresh(now = Date.now()): boolean {
  return !accessToken || now >= accessExpiresAt - sessionTuning.refreshSkewMs;
}

type RefreshOutcome = 'ok' | 'invalid' | 'error';

async function postRefresh(): Promise<RefreshOutcome> {
  let resp: Response;
  try {
    resp = await fetch(REFRESH_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return 'error';
  }
  if (resp.status === 401) return 'invalid';
  if (!resp.ok) return 'error';
  const data = (await resp.json().catch(() => null)) as { access_token?: string; expires_in?: number } | null;
  if (!data?.access_token) return 'error';
  setAccessToken(data.access_token, data.expires_in);
  return 'ok';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function refreshWithRetry(): Promise<boolean> {
  if ((await postRefresh()) === 'ok') return true;
  // Otra pestaña u otro subdominio pudo rotar la cookie en el mismo instante:
  // el navegador ya guarda la nueva, así que un reintento la usa.
  await sleep(sessionTuning.retryDelayMs);
  return (await postRefresh()) === 'ok';
}

/**
 * Renueva el access token con la cookie httpOnly. Una sola renovación a la vez
 * por pestaña (promesa compartida) y entre pestañas (Web Locks, si existe).
 */
export function refreshAccessToken(): Promise<boolean> {
  if (inflight) return inflight;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  const run: Promise<boolean> = locks
    ? // El tipo de lib.dom envuelve la promesa del callback; .then la aplana.
      locks.request(LOCK_NAME, () => refreshWithRetry()).then((ok) => ok)
    : refreshWithRetry();
  const p = run.finally(() => {
    inflight = null;
  });
  inflight = p;
  return p;
}

/** Sesión perdida sin remedio: limpia el token y avisa (una vez por pérdida). */
export function sessionLost(): void {
  setAccessToken(null);
  if (onSessionLost) onSessionLost();
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/** Instancia axios de la app (misma API /api/v1 del backend). */
export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  if (accessToken && config.headers) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

const NO_RETRY_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/google'];

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const url = original?.url ?? '';
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !NO_RETRY_PATHS.some((p) => url.includes(p))
    ) {
      original._retried = true;
      if (await refreshAccessToken()) {
        if (original.headers) original.headers.Authorization = `Bearer ${accessToken}`;
        return api.request(original);
      }
      sessionLost();
    }
    return Promise.reject(error);
  },
);

/** Código y mensaje de error del backend ({code, error}). */
export function apiErrorCode(err: unknown): { code: string; message: string; status: number } {
  const ax = err as AxiosError<{ code?: string; error?: string }>;
  return {
    status: ax?.response?.status ?? 0,
    code: ax?.response?.data?.code ?? 'NETWORK_ERROR',
    message: ax?.response?.data?.error ?? ax?.message ?? 'Error de red',
  };
}
